
import { useState } from "react";
import { Plus, Globe, Wifi, Edit2, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useUpdateNetworkRule, type NetworkRule } from "@/hooks/useNetworkRules";
import { useToast } from "@/hooks/use-toast";
import { AddUrlModal, type UrlRule, parseUrlRule } from "./AddUrlModal";

interface UrlRulesSectionProps {
  rule: NetworkRule;
}

// Helper function to extract just the base URL from a URL rule
const extractBaseUrl = (url: string): string => {
  // Special case for "block all" rule
  if (url.trim() === "*") {
    return "All network traffic";
  }
  
  // Remove negation prefix if present
  if (url.startsWith('!')) {
    const parts = url.substring(1).trim().split(' ');
    return parts[0];
  }
  
  // Split on space and take the first part (base URL)
  const parts = url.split(' ');
  return parts[0];
};

// Helper function to get URL logic display text
const getUrlLogicText = (url: string): string => {
  // Special case for "block all" rule
  if (url.trim() === "*") {
    return "Always";
  }
  
  const parsed = parseUrlRule(url);
  
  if (parsed.condition === "always") {
    return "Always";
  }
  
  if (parsed.condition === "contains") {
    return "Only when contains";
  }
  
  if (parsed.condition === "not_contains") {
    return "Except when contains";
  }
  
  return "Always";
};

// Helper function to get keywords from URL rule
const getUrlKeywords = (url: string): string[] => {
  // Special case for "block all" rule
  if (url.trim() === "*") {
    return [];
  }
  
  const parsed = parseUrlRule(url);
  return parsed.keywords || [];
};

export const UrlRulesSection = ({ rule }: UrlRulesSectionProps) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingUrl, setEditingUrl] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState<{ url: string; index: number } | null>(null);

  const { toast } = useToast();
  const updateRuleMutation = useUpdateNetworkRule();

  const handleAddUrl = async (urlRule: UrlRule) => {
    try {
      const updates = {
        urls: [...rule.urls, urlRule.url]
      };

      await updateRuleMutation.mutateAsync({ 
        ruleId: rule.id, 
        updates 
      });
      
      toast({
        title: "URL Added",
        description: `URL rule has been added successfully.`,
      });
    } catch (error) {
      console.error('Error adding URL:', error);
      toast({
        title: "Error",
        description: "Failed to add URL. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEditUrl = async (index: number) => {
    if (!editingUrl.trim()) {
      toast({
        title: "URL Required",
        description: "Please enter a URL.",
        variant: "destructive",
      });
      return;
    }

    try {
      const newUrls = [...rule.urls];
      newUrls[index] = editingUrl.trim();
      
      const updates = {
        urls: newUrls
      };

      await updateRuleMutation.mutateAsync({ 
        ruleId: rule.id, 
        updates 
      });
      
      toast({
        title: "URL Updated",
        description: "URL has been updated successfully.",
      });
      
      setEditingIndex(null);
      setEditingUrl("");
    } catch (error) {
      console.error('Error updating URL:', error);
      toast({
        title: "Error",
        description: "Failed to update URL. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEditUrlFromModal = async (urlRule: UrlRule) => {
    if (!editModalData) return;

    try {
      const newUrls = [...rule.urls];
      newUrls[editModalData.index] = urlRule.url;
      
      const updates = {
        urls: newUrls
      };

      await updateRuleMutation.mutateAsync({ 
        ruleId: rule.id, 
        updates 
      });
      
      toast({
        title: "URL Updated",
        description: "URL has been updated successfully.",
      });
      
      setEditModalData(null);
    } catch (error) {
      console.error('Error updating URL:', error);
      toast({
        title: "Error",
        description: "Failed to update URL. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteUrl = async (index: number) => {
    try {
      const newUrls = rule.urls.filter((_, i) => i !== index);
      
      const updates = {
        urls: newUrls
      };

      await updateRuleMutation.mutateAsync({ 
        ruleId: rule.id, 
        updates 
      });
      
      toast({
        title: "URL Deleted",
        description: "URL has been deleted successfully.",
      });
    } catch (error) {
      console.error('Error deleting URL:', error);
      toast({
        title: "Error",
        description: "Failed to delete URL. Please try again.",
        variant: "destructive",
      });
    }
  };

  const startEdit = (index: number, url: string) => {
    setEditingIndex(index);
    setEditingUrl(url);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingUrl("");
  };

  const openEditModal = (url: string, index: number) => {
    setEditModalData({ url, index });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            {rule.ruleType === 'webkit' ? (
              <Globe className="w-5 h-5" />
            ) : (
              <Wifi className="w-5 h-5" />
            )}
            <span>URL Rules</span>
            <Badge variant="outline" className="text-green-600">
              Socket
            </Badge>
          </CardTitle>
          <Button
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add URL
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {rule.urls.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Globe className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No URLs configured</p>
            <p className="text-sm">Click "Add URL" to get started</p>
          </div>
        ) : (
          <div className="w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-left">URL</TableHead>
                  <TableHead className="text-left">Logic</TableHead>
                  <TableHead className="text-left">Keywords</TableHead>
                  <TableHead className="text-left w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rule.urls.map((url, index) => {
                  const keywords = getUrlKeywords(url);
                  const isBlockAll = url.trim() === "*";
                  return (
                    <TableRow key={index}>
                      <TableCell className="text-left">
                        {editingIndex === index ? (
                          <Input
                            value={editingUrl}
                            onChange={(e) => setEditingUrl(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleEditUrl(index);
                              } else if (e.key === 'Escape') {
                                cancelEdit();
                              }
                            }}
                            onBlur={() => handleEditUrl(index)}
                            autoFocus
                            disabled={updateRuleMutation.isPending}
                          />
                        ) : (
                          <div className={`font-mono text-sm break-all ${isBlockAll ? 'text-red-600 font-semibold' : ''}`}>
                            {extractBaseUrl(url)}
                            {isBlockAll && (
                              <div className="text-xs text-red-500 mt-1">
                                {rule.ruleType === "webkit" 
                                  ? (rule.type === "App Specific" && rule.bundleId 
                                      ? `All network traffic for ${rule.bundleId} is blocked by default (WebKit behavior)`
                                      : "All network traffic is blocked by default (WebKit behavior)")
                                  : (rule.type === "App Specific" && rule.bundleId 
                                      ? `All network traffic for ${rule.bundleId} will be blocked`
                                      : "All network traffic will be blocked")
                                }
                              </div>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-left">
                        <div className="text-sm text-gray-600">
                          {getUrlLogicText(url)}
                        </div>
                      </TableCell>
                      <TableCell className="text-left">
                        <div className="flex flex-wrap gap-1">
                          {keywords.length > 0 ? (
                            keywords.map((keyword, keywordIndex) => (
                              <Badge key={keywordIndex} variant="secondary" className="text-xs h-6 px-2 flex items-center gap-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                {keyword}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-left w-24">
                        <div className="flex items-center space-x-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditModal(url, index)}
                            disabled={updateRuleMutation.isPending || editingIndex === index}
                            className="h-8 w-8 p-0"
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteUrl(index)}
                            disabled={updateRuleMutation.isPending}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <AddUrlModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddUrl={handleAddUrl}
        ruleType={rule.ruleType}
      />

      {editModalData && (
        <AddUrlModal
          isOpen={!!editModalData}
          onClose={() => setEditModalData(null)}
          onAddUrl={handleEditUrlFromModal}
          initialUrl={editModalData.url}
          isEditing={true}
          ruleType={rule.ruleType}
        />
      )}
    </Card>
  );
};
